import React, { useState, useMemo } from "react";
import { useData } from "@/context/DataContext";
import { PageTitle, Fab, StickyTotal, RowActions, EmptyState, SideIcon } from "@/components/shared";
import DataDialog from "@/components/DataDialog";
import { formatRupiah, parseNumber, formatDate } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Tabungan() {
  const { data, create, update, remove } = useData();
  const items = data.tabungan || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterOleh, setFilterOleh] = useState("all");
  const [filterMonth, setFilterMonth] = useState("");

  const total = items.reduce((s, i) => s + parseNumber(i.nominal), 0);
  const totalPria = items.filter((i) => i.oleh === "Pria").reduce((s, i) => s + parseNumber(i.nominal), 0);
  const totalWanita = items.filter((i) => i.oleh === "Wanita").reduce((s, i) => s + parseNumber(i.nominal), 0);

  const filtered = useMemo(() => {
    return items
      .filter((i) => (filterOleh === "all" ? true : i.oleh === filterOleh))
      .filter((i) => (filterMonth ? (i.tanggal || "").startsWith(filterMonth) : true))
      .sort((a, b) => (b.tanggal || "").localeCompare(a.tanggal || ""));
  }, [items, filterOleh, filterMonth]);

  const fields = [
    { name: "nominal", label: "Nominal (boleh negatif)", type: "currency", required: true, disabled: !!editing },
    { name: "tanggal", label: "Tanggal Simpan", type: "date", required: true },
    { name: "oleh", label: "Disimpan Oleh", type: "select", options: ["Pria", "Wanita"], required: true },
  ];

  const onSave = async (payload) => {
    if (editing) { delete payload.nominal; await update("tabungan", editing.id, payload); toast.success("Tabungan diperbarui"); }
    else { await create("tabungan", payload); toast.success("Tabungan ditambahkan"); }
  };

  return (
    <div>
      <PageTitle title="Tabungan" subtitle="Catat setiap simpanan pria & wanita" />
      <StickyTotal testId="tabungan-total">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Tabungan</p>
            <p className="font-heading text-3xl font-semibold" data-testid="tabungan-total-value">{formatRupiah(total)}</p>
          </div>
          <div className="flex gap-6 text-sm">
            <div><span className="mr-1"><SideIcon side="Pria" /></span> Pria<br /><b>{formatRupiah(totalPria)}</b></div>
            <div><span className="mr-1"><SideIcon side="Wanita" /></span> Wanita<br /><b>{formatRupiah(totalWanita)}</b></div>
          </div>
        </div>
      </StickyTotal>

      <div className="mb-4 flex flex-wrap gap-3">
        <Select value={filterOleh} onValueChange={setFilterOleh}>
          <SelectTrigger className="w-40" data-testid="filter-oleh"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            <SelectItem value="Pria">Pria</SelectItem>
            <SelectItem value="Wanita">Wanita</SelectItem>
          </SelectContent>
        </Select>
        <Input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="w-44" data-testid="filter-month" />
      </div>

      <div className="rounded-xl border border-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tanggal</TableHead><TableHead>Oleh</TableHead>
            <TableHead className="text-right">Nominal</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((i) => (
              <TableRow key={i.id} data-testid={`row-${i.id}`}>
                <TableCell>{formatDate(i.tanggal)}</TableCell>
                <TableCell><SideIcon side={i.oleh} className="mr-1" />{i.oleh}</TableCell>
                <TableCell className={`text-right font-medium ${parseNumber(i.nominal) < 0 ? "text-red-500" : ""}`}>{formatRupiah(i.nominal)}</TableCell>
                <TableCell><RowActions id={i.id} onEdit={() => { setEditing(i); setOpen(true); }} onDelete={() => remove("tabungan", i.id)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && <EmptyState />}
      </div>

      <Fab testId="fab-tabungan" onClick={() => { setEditing(null); setOpen(true); }} />
      <DataDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Tabungan" : "Tambah Tabungan"} fields={fields} initial={editing} onSave={onSave} />
    </div>
  );
}
